import { useEffect, useRef } from 'react'

/**
 * Les effets du site public.
 *
 * Une soirée masquée ne se vend pas avec une page de formulaire : le site doit
 * donner envie d'y être. Ces effets ont trois règles communes, parce qu'un
 * effet raté coûte plus cher qu'un effet absent :
 *
 * 1. **Mouvement réduit respecté.** Quiconque l'a demandé au système ne voit
 *    rien bouger — ni braises, ni inclinaison, ni transition.
 * 2. **Jamais sur le chemin critique.** Tout est décoratif (`aria-hidden`,
 *    `pointer-events: none`) ; une page sans JavaScript d'effets reste
 *    complète et utilisable.
 * 3. **Économe.** Les animations s'arrêtent hors écran et onglet caché :
 *    la batterie d'un téléphone vaut plus qu'une étincelle.
 */

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

// ── Braises qui montent ────────────────────────────────────────────

interface Ember {
  x: number
  y: number
  radius: number
  speed: number
  drift: number
  phase: number
  hue: 'ember' | 'gold'
  life: number
}

/**
 * Des braises qui montent lentement derrière le titre, comme d'un feu de
 * cheminée. Dessinées sur un `<canvas>` : quelques dizaines de points, aucun
 * nœud DOM, et une boucle qui s'arrête dès que la scène sort de l'écran.
 */
export function EmberField({ density = 1 }: { density?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || prefersReducedMotion()) return
    const context = canvas.getContext('2d')
    if (!context) return

    let width = 0
    let height = 0
    let frame = 0
    let visible = true
    let embers: Ember[] = []

    function spawn(fromBottom: boolean): Ember {
      return {
        x: Math.random() * width,
        y: fromBottom ? height + Math.random() * 40 : Math.random() * height,
        radius: 0.6 + Math.random() * 1.8,
        speed: 0.25 + Math.random() * 0.7,
        drift: (Math.random() - 0.5) * 0.4,
        phase: Math.random() * Math.PI * 2,
        hue: Math.random() < 0.65 ? 'ember' : 'gold',
        life: 0.4 + Math.random() * 0.6,
      }
    }

    function resize() {
      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      width = canvas!.clientWidth
      height = canvas!.clientHeight
      canvas!.width = Math.floor(width * ratio)
      canvas!.height = Math.floor(height * ratio)
      context!.setTransform(ratio, 0, 0, ratio, 0, 0)
      // Une braise pour ~22 000 px² : dense sur un écran large, sobre sur un téléphone.
      const count = Math.round(((width * height) / 22_000) * density)
      embers = Array.from({ length: Math.min(count, 90) }, () => spawn(false))
    }

    function tick(time: number) {
      context!.clearRect(0, 0, width, height)
      for (const ember of embers) {
        ember.y -= ember.speed
        ember.x += ember.drift + Math.sin(time / 1400 + ember.phase) * 0.25
        if (ember.y < -10) Object.assign(ember, spawn(true))

        // Elles s'éteignent en montant.
        const fade = Math.max(0, Math.min(1, ember.y / height)) * ember.life
        const flicker = 0.75 + Math.sin(time / 180 + ember.phase) * 0.25
        const alpha = fade * flicker
        const color = ember.hue === 'ember' ? '232, 90, 56' : '217, 192, 138'

        const glow = context!.createRadialGradient(
          ember.x, ember.y, 0, ember.x, ember.y, ember.radius * 5,
        )
        glow.addColorStop(0, `rgba(${color}, ${alpha})`)
        glow.addColorStop(1, `rgba(${color}, 0)`)
        context!.fillStyle = glow
        context!.beginPath()
        context!.arc(ember.x, ember.y, ember.radius * 5, 0, Math.PI * 2)
        context!.fill()
      }
      frame = requestAnimationFrame(tick)
    }

    function start() {
      if (!frame && visible && document.visibilityState === 'visible') {
        frame = requestAnimationFrame(tick)
      }
    }
    function stop() {
      cancelAnimationFrame(frame)
      frame = 0
    }

    resize()
    start()

    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting)
      if (visible) start()
      else stop()
    })
    observer.observe(canvas)
    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop())
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('resize', resize)

    return () => {
      stop()
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('resize', resize)
    }
  }, [density])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
    />
  )
}

// ── Barre de progression de lecture ────────────────────────────────

/** Un filet de braise en haut de l'écran, qui avance avec la lecture. */
export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return
    let queued = false
    function update() {
      queued = false
      const max = document.documentElement.scrollHeight - window.innerHeight
      const ratio = max > 0 ? Math.min(1, window.scrollY / max) : 0
      bar!.style.transform = `scaleX(${ratio})`
    }
    function onScroll() {
      if (!queued) {
        queued = true
        requestAnimationFrame(update)
      }
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  return (
    <div
      ref={barRef}
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[2px] origin-left scale-x-0 bg-gradient-to-r from-ember-500 via-ember-300 to-champagne-300"
    />
  )
}

// ── Inclinaison 3D au survol ───────────────────────────────────────

/**
 * Une carte qui s'incline sous le curseur, avec un reflet qui la suit.
 * Uniquement avec une souris : au doigt, une carte qui bascule gêne la lecture.
 */
export function useTilt<T extends HTMLElement>(strength = 8) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const node = ref.current
    if (!node || prefersReducedMotion() || !window.matchMedia('(hover: hover)').matches) return

    node.style.transformStyle = 'preserve-3d'
    node.style.transition = 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)'

    function onMove(event: PointerEvent) {
      const bounds = node!.getBoundingClientRect()
      const x = (event.clientX - bounds.left) / bounds.width - 0.5
      const y = (event.clientY - bounds.top) / bounds.height - 0.5
      node!.style.transform =
        `perspective(900px) rotateX(${(-y * strength).toFixed(2)}deg) ` +
        `rotateY(${(x * strength).toFixed(2)}deg) translateZ(0)`
      node!.style.setProperty('--mx', `${event.clientX - bounds.left}px`)
      node!.style.setProperty('--my', `${event.clientY - bounds.top}px`)
    }
    function onLeave() {
      node!.style.transform = ''
    }

    node.addEventListener('pointermove', onMove)
    node.addEventListener('pointerleave', onLeave)
    return () => {
      node.removeEventListener('pointermove', onMove)
      node.removeEventListener('pointerleave', onLeave)
    }
  }, [strength])

  return ref
}

/** Un bouton qui se laisse attirer par le curseur — réservé aux appels principaux. */
export function useMagnetic<T extends HTMLElement>(pull = 0.25) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const node = ref.current
    if (!node || prefersReducedMotion() || !window.matchMedia('(hover: hover)').matches) return
    node.style.transition = 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1)'

    function onMove(event: PointerEvent) {
      const bounds = node!.getBoundingClientRect()
      const x = event.clientX - (bounds.left + bounds.width / 2)
      const y = event.clientY - (bounds.top + bounds.height / 2)
      node!.style.transform = `translate(${x * pull}px, ${y * pull}px)`
    }
    function onLeave() {
      node!.style.transform = ''
    }
    node.addEventListener('pointermove', onMove)
    node.addEventListener('pointerleave', onLeave)
    return () => {
      node.removeEventListener('pointermove', onMove)
      node.removeEventListener('pointerleave', onLeave)
    }
  }, [pull])

  return ref
}

// ── Parallaxe ──────────────────────────────────────────────────────

/** Un élément qui glisse moins vite que la page — de la profondeur, sans 3D. */
export function useParallax<T extends HTMLElement>(speed = 0.25) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const node = ref.current
    if (!node || prefersReducedMotion()) return
    let queued = false
    function update() {
      queued = false
      node!.style.transform = `translate3d(0, ${(window.scrollY * speed).toFixed(1)}px, 0)`
    }
    function onScroll() {
      if (!queued) {
        queued = true
        requestAnimationFrame(update)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [speed])

  return ref
}

// ── Gerbe de célébration ───────────────────────────────────────────

/**
 * Une gerbe d'éclats or et braise, au moment où quelque chose a RÉUSSI : un
 * vote enregistré, une candidature déposée, une commande confirmée. Elle dit
 * « c'est fait » mieux qu'un texte vert — et s'efface seule en deux secondes.
 */
export function celebrate(origin?: { x: number; y: number }) {
  if (typeof document === 'undefined' || prefersReducedMotion()) return

  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100vw',
    height: '100vh',
    pointerEvents: 'none',
    zIndex: '9999',
  })
  document.body.appendChild(canvas)
  const ratio = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = window.innerWidth * ratio
  canvas.height = window.innerHeight * ratio
  const context = canvas.getContext('2d')
  if (!context) {
    canvas.remove()
    return
  }
  context.scale(ratio, ratio)

  const start = origin ?? { x: window.innerWidth / 2, y: window.innerHeight * 0.45 }
  const colors = ['#d9c08a', '#ebdcb8', '#e85a38', '#f2913a', '#c2a464']
  const pieces = Array.from({ length: 90 }, () => {
    const angle = Math.random() * Math.PI * 2
    const velocity = 4 + Math.random() * 8
    return {
      x: start.x,
      y: start.y,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity - 4,
      size: 3 + Math.random() * 5,
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.3,
      color: colors[Math.floor(Math.random() * colors.length)]!,
    }
  })

  const began = performance.now()
  function frame(now: number) {
    const elapsed = now - began
    context!.clearRect(0, 0, window.innerWidth, window.innerHeight)
    const alpha = Math.max(0, 1 - elapsed / 1900)
    for (const piece of pieces) {
      piece.vy += 0.22
      piece.vx *= 0.985
      piece.x += piece.vx
      piece.y += piece.vy
      piece.rotation += piece.spin
      context!.save()
      context!.globalAlpha = alpha
      context!.translate(piece.x, piece.y)
      context!.rotate(piece.rotation)
      context!.fillStyle = piece.color
      context!.fillRect(-piece.size / 2, -piece.size / 4, piece.size, piece.size / 2)
      context!.restore()
    }
    if (alpha > 0) requestAnimationFrame(frame)
    else canvas.remove()
  }
  requestAnimationFrame(frame)
}
